import React, { useState } from 'react';
import { Pencil, Power, PowerOff, Star, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CancelButton, SaveChangesButton, StandardButton } from '@/styles/StandardButtons';
import { colors } from '@/design-system/colors';
import { VendorField } from '@/components/shared/VendorField';
import { formatPrice, newOfferRow, type OfferRow } from '../lib/offers';

interface ItemOffersSectionProps {
  rows: OfferRow[];
  onChange: (rows: OfferRow[]) => void;
  /** a vendor was taken off the item (the popup remembers a saved one so its offer is deleted on Save) */
  onRemove: (row: OfferRow) => void;
}

interface Draft {
  /** key of the row being edited; null = a new vendor */
  key: string | null;
  vendorId: string;
  vendorLabel: string;
  unitPrice: string;
  vendorSku: string;
}

const emptyDraft = (): Draft => ({ key: null, vendorId: '', vendorLabel: '', unitPrice: '', vendorSku: '' });

/** An icon-only action of a vendor row: the native `title` (the app's own hover hint, as on the search button) says what it does, `.generic-icon-button` (design-system/forms.css) is its look and hover. */
function RowAction({ tip, label, onClick, disabled, pressed, children }: { tip: string; label: string; onClick: () => void; disabled?: boolean; pressed?: boolean; children: React.ReactNode }) {
  return (
    <button type="button" className="generic-icon-button" title={tip} aria-label={label} aria-pressed={pressed} disabled={disabled} onClick={onClick}>
      {children}
    </button>
  );
}

/**
 * The vendors that sell ONE item and their prices, inside the item's popup. Controlled: every change (add, edit, star, deactivate, remove)
 * only changes the popup's rows; nothing is written until the popup's single Save persists the item and all its vendors together.
 * "Remove" takes the vendor's offer off THIS item; it never deletes the vendor from the company.
 */
export function ItemOffersSection({ rows, onChange, onRemove }: ItemOffersSectionProps) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const patchDraft = (patch: Partial<Draft>) => setDraft((current) => (current ? { ...current, ...patch } : current));
  const patchRow = (key: string, patch: Partial<OfferRow>) => onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const togglePreferred = (row: OfferRow) =>
    onChange(rows.map((r) => (r.key === row.key ? { ...r, isPreferred: !row.isPreferred } : { ...r, isPreferred: false })));

  const toggleActive = (row: OfferRow) => patchRow(row.key, { isActive: !row.isActive, isPreferred: row.isActive ? false : row.isPreferred });

  // a saved offer is deleted for real on Save, so it asks first (the app's own confirmation); a vendor that was never saved just leaves the list
  const remove = (row: OfferRow) => {
    if (row.id && !window.confirm(`Remove ${row.vendorLabel} and its price from this item? The vendor itself is not deleted.`)) return;
    onRemove(row);
  };

  // the pencil and a click on the vendor's name do the same: edit THIS item's price and SKU for that vendor
  const startEdit = (row: OfferRow) => {
    setError(null);
    setDraft({ key: row.key, vendorId: row.vendorId, vendorLabel: row.vendorLabel, unitPrice: row.unitPrice, vendorSku: row.vendorSku });
  };

  const submitDraft = () => {
    if (!draft) return;
    const price = draft.unitPrice.trim();
    const editing = draft.key ? rows.find((row) => row.key === draft.key) : null;
    if (!editing && !draft.vendorId) { setError('Select a vendor'); return; }
    if (price === '' || !Number.isFinite(Number(price)) || Number(price) < 0) { setError('Enter a unit price (0 or more)'); return; }
    if (!editing && rows.some((row) => row.vendorId === draft.vendorId)) { setError('This vendor is already in the list'); return; }
    if (editing) patchRow(editing.key, { unitPrice: price, vendorSku: draft.vendorSku });
    else onChange([...rows, newOfferRow({ vendorId: draft.vendorId, vendorLabel: draft.vendorLabel, unitPrice: price, vendorSku: draft.vendorSku })]);
    setDraft(null);
    setError(null);
  };

  return (
    // Enter inside these fields must not submit the item form that contains this section
    <div className="space-y-3" data-testid="item-offers" onKeyDown={(event) => { if (event.key === 'Enter' && (event.target as HTMLElement).tagName === 'INPUT') event.preventDefault(); }}>
      <div className="flex items-center justify-between">
        <div className="font-bold">Vendors</div>
        {!draft && <StandardButton type="button" customVariant="blue" onClick={() => { setError(null); setDraft(emptyDraft()); }}>Add Vendor</StandardButton>}
      </div>

      {error && <div className="generic-form-error" role="alert">{error}</div>}

      {rows.length > 0 && (
        <ul className="text-sm divide-y divide-border">
          {rows.map((row) => (
            <li key={row.key} className={`flex items-center gap-3 py-1.5 ${row.isActive ? '' : 'text-gray-400'}`} data-testid={`offer-${row.key}`}>
              <button
                type="button"
                className="min-w-0 flex-1 truncate rounded px-2 py-1 text-left"
                aria-label={`Edit price of ${row.vendorLabel}`}
                onClick={() => startEdit(row)}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.buttonTertiaryHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
              >
                <span className="font-medium">{row.vendorLabel}</span>
                {row.vendorSku && <span className="text-gray-500"> · {row.vendorSku}</span>}
                {!row.isActive && <span> · Inactive</span>}
              </button>
              <div className="tabular-nums">{formatPrice(Number(row.unitPrice))}</div>
              <div className="flex items-center">
                <RowAction
                  tip={row.isActive ? (row.isPreferred ? 'Preferred vendor. Click to clear' : 'Make this the preferred vendor') : "An inactive vendor can't be the preferred one"}
                  label={row.isPreferred ? 'Preferred vendor' : 'Make preferred'}
                  pressed={row.isPreferred}
                  disabled={!row.isActive}
                  onClick={() => togglePreferred(row)}
                >
                  <Star className={`w-4 h-4 ${row.isPreferred ? 'fill-current' : ''}`} />
                </RowAction>
                <RowAction
                  tip="Edit this vendor's price and SKU"
                  label={`Edit ${row.vendorLabel}`}
                  onClick={() => startEdit(row)}
                >
                  <Pencil className="w-4 h-4" />
                </RowAction>
                {row.id && (
                  <RowAction
                    tip={row.isActive ? 'Deactivate: keep it on the item, out of the price range' : "Activate this vendor's price"}
                    label={row.isActive ? `Deactivate ${row.vendorLabel}` : `Activate ${row.vendorLabel}`}
                    onClick={() => toggleActive(row)}
                  >
                    {row.isActive ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                  </RowAction>
                )}
                <RowAction tip="Remove this vendor and its price from the item" label={`Remove ${row.vendorLabel}`} onClick={() => remove(row)}>
                  <Trash2 className="w-4 h-4" />
                </RowAction>
              </div>
            </li>
          ))}
        </ul>
      )}

      {draft && (
        <div className="border-2 border-border rounded-md p-4 space-y-4" data-testid="offer-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="offer_vendor" className="font-form-label">Vendor</Label>
              {draft.key ? (
                <div className="text-sm py-2">{draft.vendorLabel}</div>
              ) : (
                <VendorField id="offer_vendor" value={draft.vendorLabel} allowFreeText={false} placeholder="Select vendor" onChange={({ id, name }) => patchDraft({ vendorId: id || '', vendorLabel: name })} />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="offer_price" className="font-form-label">Unit price ($)</Label>
              <Input id="offer_price" type="number" step="0.01" min="0" inputMode="decimal" value={draft.unitPrice} onChange={(e) => patchDraft({ unitPrice: e.target.value })} className="font-form-input generic-form-field" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="offer_vendor_sku" className="font-form-label">Vendor SKU</Label>
              <Input id="offer_vendor_sku" value={draft.vendorSku} onChange={(e) => patchDraft({ vendorSku: e.target.value })} className="font-form-input generic-form-field" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <CancelButton onClick={() => { setDraft(null); setError(null); }}>Cancel</CancelButton>
            <SaveChangesButton type="button" onClick={submitDraft}>{draft.key ? 'Save' : 'Add Vendor'}</SaveChangesButton>
          </div>
        </div>
      )}

    </div>
  );
}
